import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import { hasRequiredName } from "../../utils/validators";
import CloseIcon from "@mui/icons-material/Close";
import { v4 as uuid } from 'uuid';
import DropdownTextField from "../form-components/DropdownTextField";
import LocalText from "../common/LocalText";

const CommentModal = ({
  open,
  onClose,
  onSave,
  locales,
  initialData,
  readOnly = false,
  approvalMode = false,
}) => {
  const isCreate = !initialData;
  const mode = isCreate ? "create" : "edit";
  const [localizedPairs, setLocalizedPairs] = useState({ key: uuid(), values: [] });

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setLocalizedPairs(initialData.localizedPairs ?? { key: uuid(), values: [] });
    } else {
      setLocalizedPairs({ key: uuid(), values: [] });
    }
  }, [open, initialData]);

  const handleSave = () => {
    onSave(localizedPairs, initialData?.id);
    if (isCreate) setLocalizedPairs({ key: uuid(), values: [] });
    onClose();
  };

  const title = readOnly
    ? "View Comment"
    : mode === "create"
      ? "Add Comment"
      : approvalMode
        ? "Propose Edit"
        : "Edit Comment";

  const saveLabel = mode === "create" ? "Save" : approvalMode ? "Submit for Approval" : "Update";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogTitle sx={{ bgcolor: "primary.main", color: "white", pr: 6, py: 2.5 }}>
        <Typography variant="h6" fontWeight={700} component="span">
          <LocalText>{title}</LocalText>
        </Typography>
        <IconButton
          onClick={(e) => { e.currentTarget.blur(); onClose(); }}
          size="small"
          sx={{ position: "absolute", right: 10, top: 12, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', mb: 0.5 }}>
          <LocalText>Comment</LocalText>
          {!readOnly && <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>}
        </Typography>
        <DropdownTextField
          label="Comment"
          values={localizedPairs?.values}
          onChange={(newValues) => setLocalizedPairs((prev) => ({ ...prev, values: newValues }))}
          options={locales}
          attribute="value"
          disabled={readOnly}
          required={!readOnly}
        />
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", p: 3, bgcolor: "grey.50" }}>
        <Button onClick={onClose} sx={{ mr: 2 }}>
          <LocalText>{readOnly ? "Close" : "Cancel"}</LocalText>
        </Button>
        {!readOnly && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!hasRequiredName(localizedPairs?.values)}
            size="large"
            sx={{ px: 6, py: 1.5, fontWeight: 700, borderRadius: 2 }}
          >
            <LocalText>{saveLabel}</LocalText>
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CommentModal;
